/**
 * The client's mirror of server state.
 *
 * **The server is authoritative** (SPEC §4.2). Nothing in this file computes a
 * score, a ranking, a leaderboard position, or a phase transition. Every field
 * below is either (a) something the server told us, (b) local UI draft state
 * that has not been sent yet, or (c) connection bookkeeping. If you find
 * yourself wanting to derive a game outcome here, don't — wait for the frame.
 *
 * Shape of the flow:
 *
 *   screen  --intent-->  store action  -->  GameSocket.send()  -->  DO
 *   screen  <--render--  store state   <--  applyServerMessage  <--  DO
 *
 * `snapshot` is the spine. Every message that could change the world carries a
 * fresh snapshot, and reconnecting replaces the whole thing — which is exactly
 * why a dropped socket is invisible (SPEC §5.4).
 */
import { create } from 'zustand';
import {
  DEFAULT_DECK_ID,
  DEFAULT_ROUNDS,
  ESTIMATED_MS_PER_TURN,
  LOCK_COUNTDOWN_MS,
  MIN_ROUNDS,
  estimateDurationMs,
  type ClientMessage,
  type ClientMessageOf,
  type ErrorCode,
  type Leaderboard,
  type PhotoDataUrl,
  type PlayerId,
  type Prediction,
  type Question,
  type QuestionNumber,
  type Ranking,
  type RankingHistory,
  type RevealPayload,
  type RoomSettings,
  type RoomSnapshot,
  type RosterEntry,
  type ServerMessage,
  type Superlative,
  type ThumbsVote,
} from '@idgame/protocol';
import { GameSocket, type ConnectionStatus, type GameSocketOptions } from '../net/socket.js';
import { clearHostToken, clearSession, loadSession, markOnboarded } from '../net/session.js';
import { sound } from '../lib/audio.js';

/* -------------------------------------------------------------------------- */
/* Types                                                                       */
/* -------------------------------------------------------------------------- */

export interface RoomError {
  code: ErrorCode;
  message: string;
  fatal: boolean;
  at: number;
}

/** The active 10-second guillotine (SPEC §2.3 phase 3). */
export interface LockState {
  /** ms epoch **on the server clock**. Use `serverToLocal` before comparing. */
  endsAt: number;
  durationMs: number;
  /** Turn index the lock belongs to, so a stale lock cannot leak forward. */
  turnIndex: number | null;
}

/** Local, pre-submission onboarding state. Never sent until the user commits. */
export interface DraftProfile {
  /** True once the user has passed the explicit camera consent screen (§6). */
  cameraConsent: boolean;
  name: string;
  /** Processed data URL, already resized/compressed. */
  photo: PhotoDataUrl | null;
  /** How the photo was obtained — drives the "one retake" affordance. */
  photoSource: 'camera' | 'upload' | null;
  /** SPEC §6: exactly one retake is offered on the live-selfie path. */
  retakesUsed: number;
}

export type OnboardingStep = 'consent' | 'capture' | 'name' | 'done';

export interface GameState {
  /* --- connection ------------------------------------------------------ */
  status: ConnectionStatus;
  /** Consecutive failed connection attempts; 0 when healthy. */
  attempt: number;
  /** True once we have ever been open for this room — hides the first-load flash. */
  everConnected: boolean;
  /** `serverNow - Date.now()`, updated from every snapshot and pong. */
  serverOffsetMs: number;
  /** Round-trip time from the last pong, for countdown fidelity. */
  rttMs: number | null;

  /* --- identity -------------------------------------------------------- */
  roomCode: string | null;
  selfId: PlayerId | null;
  isHost: boolean;

  /* --- server state ---------------------------------------------------- */
  snapshot: RoomSnapshot | null;
  /** Photos arrive separately from the roster because they are large (§6). */
  photos: Record<PlayerId, PhotoDataUrl | null>;
  /** From the `settings` message; the lobby also computes a local preview. */
  estimatedDurationMs: number;
  /**
   * The server's authoritative ceiling on rounds — `floor(remaining / players)`,
   * clamped to the protocol's 1–10.
   *
   * Rendered, never recomputed. The client used to derive this from the full
   * 100-number deck while the server divided only the *unburned remainder*;
   * identical on a fresh game, wrong after a play-again, and the lobby would
   * offer rounds the server rejects. Starts at `MIN_ROUNDS` so the stepper can
   * never offer more than the server allows before the first `settings` frame —
   * which the server sends immediately after `welcome`.
   */
  maxRounds: number;
  /**
   * True when "play again" wiped the numbered grid because too few numbers were
   * left for another game (SPEC §2.6). Set explicitly by the server rather than
   * inferred from `usedQuestionNumbers` emptying, so the beat is shown
   * deliberately and not guessed from a transition a reconnect might miss.
   */
  deckReshuffled: boolean;
  /** Latest reveal payload (SPEC §2.3 phase 4). Cleared on the next turn. */
  reveal: RevealPayload | null;
  /**
   * Justification screen payload (phase 5), including our own vote state and
   * the completed ranking.
   *
   * The ranking is on this frame, not read back off `reveal`, because `reveal`
   * is nulled the instant the phase changes — and because a phone that
   * reconnects mid-argument never saw the reveal at all. SPEC §5.2 requires
   * every player to have the order in front of them while the turn player
   * argues; that is only true if it survives a reconnect.
   */
  justification: {
    turnPlayerId: PlayerId;
    question: Question;
    selfVoted: boolean;
    ranking: Ranking;
  } | null;
  /** End-of-round leaderboard (SPEC §2.4). Never shown mid-round. */
  leaderboard: Leaderboard | null;
  /** Null after the final round. */
  nextRound: number | null;
  superlatives: Superlative[] | null;
  /**
   * Every completed ranking of the game (SPEC §2.6), oldest first, as sent with
   * the `superlatives` frame. In-memory only — never persisted (SPEC §6).
   */
  rankingHistory: RankingHistory;
  gameEndedReason: 'completed' | 'host_ended' | 'abandoned' | null;
  /** The active lock countdown, or null. */
  lock: LockState | null;
  /** Our own prediction as the server has it, plus whether it can still change. */
  selfPrediction: Prediction | null;
  selfPredictionEditable: boolean;
  /**
   * True once **this** seat has sent `lock_prediction` — "I am done, stop
   * waiting for me" (SPEC §2.3 phase 3).
   *
   * Kept apart from `selfPredictionEditable`, which the room's hard lock also
   * turns off. The two mean different things on the screen: locking is what
   * swaps the face grid for the room board, and the guillotine falling on a
   * player who never pressed Submit must not do that — DESIGN §3.5 is explicit
   * that nothing under the strip moves, resizes or changes on the way to zero.
   *
   * It is also **not** inferred from `prediction_ack.editable`. The server
   * replays that frame with `editable: false` to anyone reconnecting into
   * `lock-countdown`, locked or not, so reading a lock out of it would invent
   * one.
   */
  selfPredictionLocked: boolean;
  /** Host-only: set when the turn player has been gone > grace (SPEC §5.4). */
  skipOffer: PlayerId | null;
  /**
   * The seat picker (SPEC §5.4): empty seats this device has been offered
   * because it arrived without a session token, or null when it was not
   * offered any.
   *
   * Non-null means **we have no seat yet** and the room is waiting for an
   * answer — `RoomGate` renders the picker instead of the spinner, and it is
   * cleared by the `welcome` that follows a successful claim.
   */
  claimableSeats: { playerId: PlayerId; name: string }[] | null;
  /** Whether the picker may offer "none of these — I'm new". */
  canJoinAsNew: boolean;
  /** Most recent server error, for a toast. */
  error: RoomError | null;

  /* --- local UI state -------------------------------------------------- */
  draft: DraftProfile;
  muted: boolean;
  /**
   * True once this seat has been given a name **and** a photo — i.e. the
   * consent → camera → name flow is behind us. Seeded from the stored session
   * on reconnect, so returning to the URL drops straight back into the game
   * rather than asking for a selfie again (SPEC §5.4).
   */
  profileSubmitted: boolean;
}

export interface GameActions {
  /* connection */
  connect: (roomCode: string, options?: Partial<GameSocketOptions>) => void;
  disconnect: () => void;
  /** Abandon the seat entirely (forget the token) and disconnect. */
  leaveRoom: () => void;
  /**
   * Answer the seat picker: take `playerId`'s empty seat, or omit it to ask
   * for a fresh seat instead (SPEC §5.4).
   */
  claimSeat: (playerId?: PlayerId) => void;

  /* onboarding, local only */
  grantCameraConsent: () => void;
  setDraftName: (name: string) => void;
  setDraftPhoto: (photo: PhotoDataUrl, source: 'camera' | 'upload') => void;
  clearDraftPhoto: (options?: { countsAsRetake?: boolean }) => void;
  resetDraft: () => void;

  /* intents -> server */
  submitProfile: (input: { name?: string; photo?: PhotoDataUrl }) => void;
  updateSettings: (settings: { rounds?: number; deckId?: string }) => void;
  startGame: () => void;
  selectQuestion: (number: QuestionNumber) => void;
  submitRanking: (ranking: Ranking) => void;
  submitPrediction: (prediction: Prediction) => void;
  /** "I am done." Final, one-way, and carries no picks (SPEC §2.3 phase 3). */
  lockPrediction: () => void;
  thumbsVote: (vote: ThumbsVote) => void;
  justificationDone: () => void;
  /* host controls (SPEC §5.5) */
  forceAdvance: () => void;
  skipTurn: (playerId: PlayerId) => void;
  removePlayer: (playerId: PlayerId) => void;
  endGame: () => void;
  playAgain: () => void;

  /* misc */
  setMuted: (muted: boolean) => void;
  toggleMuted: () => void;
  dismissError: () => void;
  /** Escape hatch used by tests and by the socket wiring. */
  applyServerMessage: (message: ServerMessage) => void;
  /** Server clock -> local clock, for countdown maths. */
  serverToLocal: (serverEpochMs: number) => number;
}

export type GameStore = GameState & GameActions;

/* -------------------------------------------------------------------------- */
/* Initial state                                                               */
/* -------------------------------------------------------------------------- */

const EMPTY_DRAFT: DraftProfile = {
  cameraConsent: false,
  name: '',
  photo: null,
  photoSource: null,
  retakesUsed: 0,
};

const DEFAULT_SETTINGS: RoomSettings = { rounds: DEFAULT_ROUNDS, deckId: DEFAULT_DECK_ID };

const INITIAL: GameState = {
  status: 'idle',
  attempt: 0,
  everConnected: false,
  serverOffsetMs: 0,
  rttMs: null,

  roomCode: null,
  selfId: null,
  isHost: false,

  snapshot: null,
  photos: {},
  estimatedDurationMs: 0,
  maxRounds: MIN_ROUNDS,
  deckReshuffled: false,
  reveal: null,
  justification: null,
  leaderboard: null,
  nextRound: null,
  superlatives: null,
  rankingHistory: [],
  gameEndedReason: null,
  lock: null,
  selfPrediction: null,
  selfPredictionEditable: true,
  selfPredictionLocked: false,
  skipOffer: null,
  claimableSeats: null,
  canJoinAsNew: false,
  error: null,

  draft: { ...EMPTY_DRAFT },
  muted: sound.muted,
  profileSubmitted: false,
};

/* -------------------------------------------------------------------------- */
/* The socket lives outside the store                                          */
/* -------------------------------------------------------------------------- */

let socket: GameSocket | null = null;
let unsubscribers: (() => void)[] = [];

/** Test seam: the live socket, or null. */
export function currentSocket(): GameSocket | null {
  return socket;
}

function teardownSocket(): void {
  for (const off of unsubscribers) off();
  unsubscribers = [];
  socket?.close();
  socket = null;
}

/* -------------------------------------------------------------------------- */
/* Store                                                                       */
/* -------------------------------------------------------------------------- */

export const useGameStore = create<GameStore>()((set, get) => {
  /** Send an intent, or drop it silently if we have no socket at all. */
  const send = (message: ClientMessage): void => {
    socket?.send(message);
  };

  return {
    ...INITIAL,

    /* ---------------------------------------------------------------- */
    /* Connection                                                        */
    /* ---------------------------------------------------------------- */

    connect(roomCode, options) {
      const code = roomCode.trim().toUpperCase();
      if (socket && socket.roomCode === code) {
        socket.connect();
        return;
      }

      teardownSocket();

      // A returning player's seat already carries their name and photo on the
      // server. Only a boolean is stored on the device (SPEC §6) — the name
      // comes back in the roster like everyone else's.
      const stored = loadSession(code);
      set({
        ...INITIAL,
        muted: sound.muted,
        roomCode: code,
        status: 'connecting',
        profileSubmitted: stored?.onboarded === true,
        draft: { ...EMPTY_DRAFT, cameraConsent: stored?.onboarded === true },
      });

      const next = new GameSocket({
        roomCode: code,
        // Lets the socket discard queued turn-scoped intents whose turn the
        // room has already left, rather than replaying them into a new turn.
        currentTurnIndex: () => get().snapshot?.turn?.index ?? null,
        ...options,
      });
      socket = next;

      unsubscribers.push(
        next.on('status', (status, detail) => {
          set((state) => ({
            status,
            attempt: detail.attempt,
            everConnected: state.everConnected || status === 'open',
          }));
        }),
        next.on('message', (message) => {
          get().applyServerMessage(message);
          confirmOnboarding(get());
        }),
        next.on('protocolError', (error, discriminator) => {
          // A frame the schema rejects is a bug on one side of the wire. The
          // payload is never logged — see `SocketEvents.protocolError`.
          if (import.meta.env?.DEV) {
            console.error(`[protocol] rejected server frame "${discriminator}": ${error}`);
          }
        }),
      );

      next.connect();
    },

    disconnect() {
      teardownSocket();
      set({ status: 'idle' });
    },

    leaveRoom() {
      const { roomCode } = get();
      if (roomCode) {
        clearSession(roomCode);
        // Leaving is deliberate: coming back later should not take the host
        // seat off whoever has been running the room since (SPEC §5.5).
        clearHostToken(roomCode);
      }
      teardownSocket();
      set({ ...INITIAL, muted: sound.muted });
    },

    claimSeat(playerId) {
      // Cleared optimistically so the picker cannot be tapped twice while the
      // answer is in flight. A refusal (`seat_taken`) re-offers what is left,
      // which puts it straight back.
      set({ claimableSeats: null });
      socket?.claimSeat(playerId);
    },

    /* ---------------------------------------------------------------- */
    /* Local onboarding draft                                            */
    /* ---------------------------------------------------------------- */

    grantCameraConsent() {
      set((state) => ({ draft: { ...state.draft, cameraConsent: true } }));
    },

    setDraftName(name) {
      set((state) => ({ draft: { ...state.draft, name } }));
    },

    setDraftPhoto(photo, source) {
      set((state) => ({ draft: { ...state.draft, photo, photoSource: source } }));
    },

    clearDraftPhoto(options) {
      set((state) => ({
        draft: {
          ...state.draft,
          photo: null,
          photoSource: null,
          retakesUsed: options?.countsAsRetake
            ? state.draft.retakesUsed + 1
            : state.draft.retakesUsed,
        },
      }));
    },

    resetDraft() {
      set({ draft: { ...EMPTY_DRAFT } });
    },

    /* ---------------------------------------------------------------- */
    /* Intents                                                           */
    /* ---------------------------------------------------------------- */

    submitProfile({ name, photo }) {
      if (name === undefined && photo === undefined) return;
      const message: ClientMessageOf<'set_profile'> = { t: 'set_profile' };
      if (name !== undefined) message.name = name;
      if (photo !== undefined) message.photo = photo;
      send(message);

      // Local only, so the flow can move on without waiting for a round trip.
      // The **persisted** flag is set from the server's roster instead
      // (`confirmOnboarding`), so a `set_profile` that never landed cannot make
      // a later reconnect skip the photo step.
      const { draft } = get();
      const haveName = name !== undefined || draft.name.length > 0;
      const havePhoto = photo !== undefined || draft.photo !== null;
      if (haveName && havePhoto) set({ profileSubmitted: true });
    },

    updateSettings(settings) {
      const message: ClientMessageOf<'update_settings'> = { t: 'update_settings' };
      if (settings.rounds !== undefined) message.rounds = settings.rounds;
      if (settings.deckId !== undefined) message.deckId = settings.deckId;
      send(message);
    },

    startGame() {
      send({ t: 'start_game' });
    },

    selectQuestion(number) {
      send({ t: 'select_question', number });
    },

    submitRanking(ranking) {
      send({ t: 'submit_ranking', ranking });
    },

    submitPrediction(prediction) {
      // Optimistic only in the sense of "we show your own picks back to you".
      // Scoring, locking and validity are all the server's call.
      //
      // Still sent on **every** complete pair, and deliberately unchanged by
      // Submit becoming a lock: this is what keeps the pair on record, and a
      // complete pair scores whether or not the player ever locked in
      // (SPEC §2.3 phase 3). The two messages answer different questions.
      set({ selfPrediction: prediction });
      send({ t: 'submit_prediction', top: prediction.top, bottom: prediction.bottom });
    },

    lockPrediction() {
      /*
        Refused locally when there is nothing to lock, because the server
        refuses it too ("pick both ends before locking in") and a rejected lock
        would leave this phone showing a one-way door it never actually went
        through. The screen only offers Submit on a complete pair, so this is a
        belt-and-braces guard rather than a path anyone can take.

        Set here rather than waited for. Every other intent in this file lets
        the server answer first, and this one cannot: the answer is
        `prediction_ack { editable: false }`, which is exactly the frame a
        reconnect into `lock-countdown` also produces for a player who has NOT
        locked, so the ack cannot be read as confirmation. What makes it safe is
        that the lock is one-way — there is no server reply that would take it
        back, and the snapshot only ever agrees.
      */
      if (!get().selfPrediction) return;
      set({ selfPredictionLocked: true, selfPredictionEditable: false });
      send({ t: 'lock_prediction' });
    },

    thumbsVote(vote) {
      set((state) =>
        state.justification ? { justification: { ...state.justification, selfVoted: true } } : {},
      );
      send({ t: 'thumbs_vote', vote });
    },

    justificationDone() {
      send({ t: 'justification_done' });
    },

    forceAdvance() {
      send({ t: 'force_advance' });
    },

    skipTurn(playerId) {
      send({ t: 'skip_turn', playerId });
    },

    removePlayer(playerId) {
      send({ t: 'remove_player', playerId });
    },

    endGame() {
      send({ t: 'end_game' });
    },

    playAgain() {
      send({ t: 'play_again' });
    },

    /* ---------------------------------------------------------------- */
    /* Misc                                                              */
    /* ---------------------------------------------------------------- */

    setMuted(muted) {
      sound.setMuted(muted);
      set({ muted });
    },

    toggleMuted() {
      const muted = sound.toggleMuted();
      set({ muted });
    },

    dismissError() {
      set({ error: null });
    },

    serverToLocal(serverEpochMs) {
      return serverEpochMs - get().serverOffsetMs;
    },

    /* ---------------------------------------------------------------- */
    /* The reducer                                                       */
    /* ---------------------------------------------------------------- */

    applyServerMessage(message) {
      set((state) => reduce(state, message));
    },
  };
});

/* -------------------------------------------------------------------------- */
/* Reducer                                                                     */
/* -------------------------------------------------------------------------- */

function withSnapshot(state: GameState, snapshot: RoomSnapshot): Partial<GameState> {
  const previous = state.snapshot;
  const turnChanged = previous?.turn?.index !== snapshot.turn?.index;

  /*
    A lock is one-way, so within a turn this only ever ratchets up. The snapshot
    is authoritative about a lock it knows about, but it can be *older* than one
    this phone has just sent — a `phase_changed` built a beat before the lock
    was processed would otherwise hand the player their answers back, and there
    is no way back. Across a turn boundary the snapshot is the only word.
  */
  const locked = snapshot.selfPredictionLocked || (!turnChanged && state.selfPredictionLocked);

  /*
    Editable means one thing: the server would accept an edit. That is both
    prediction phases and neither of the others — the ten seconds of the
    countdown are ten seconds in which an unlocked predictor may still change an
    end (DESIGN §3.5), and a locked one may not, in either phase.
  */
  const predicting = snapshot.phase === 'rank-and-predict' || snapshot.phase === 'lock-countdown';

  return {
    snapshot,
    serverOffsetMs: snapshot.serverNow - Date.now(),
    selfPredictionLocked: locked,
    selfPredictionEditable: predicting && !locked,
    estimatedDurationMs: estimateDurationMs(
      countActivePlayers(snapshot.players),
      snapshot.settings.rounds,
      ESTIMATED_MS_PER_TURN,
    ),
    ...phaseScopedState(state, snapshot, turnChanged),
  };
}

/**
 * Everything that belongs to a specific turn or phase, recomputed from the
 * snapshot.
 *
 * A snapshot is the reconnect contract: whatever it says is true, and anything
 * it contradicts must go. Two things this has to get right that a naive
 * "wipe on turn change" does not:
 *
 *  - **The lock survives a reconnect.** `turn.lockEndsAt` is in the snapshot, so
 *    a client that reloads mid-guillotine reconstructs the countdown instead of
 *    rendering nothing while the clock runs out on them.
 *  - **Stale state dies on a phase change within the same turn.** Lock →
 *    reveal → justification are all one turn, so keying only on turn index left
 *    a finished lock running underneath the reveal.
 *
 * The snapshot carries no reveal/leaderboard payload of its own — Agent A
 * re-emits those as their own frames after `welcome`, and the reducer applies
 * them normally.
 */
function phaseScopedState(
  state: GameState,
  snapshot: RoomSnapshot,
  turnChanged: boolean,
): Partial<GameState> {
  const phaseChanged = state.snapshot?.phase !== snapshot.phase;
  const stale = turnChanged || phaseChanged;
  const turn = snapshot.turn;

  // The countdown is live if and only if the server says we are in it.
  let lock: LockState | null = stale ? null : state.lock;
  if (snapshot.phase === 'lock-countdown' && turn?.lockEndsAt != null) {
    lock =
      state.lock?.endsAt === turn.lockEndsAt
        ? state.lock
        : { endsAt: turn.lockEndsAt, durationMs: LOCK_COUNTDOWN_MS, turnIndex: turn.index };
  } else if (snapshot.phase !== 'lock-countdown') {
    lock = null;
  }

  return {
    lock,
    // These are only meaningful in their own phase; Agent A re-sends the
    // payload when a reconnect lands inside one.
    reveal: snapshot.phase === 'reveal' && !turnChanged ? state.reveal : null,
    justification: snapshot.phase === 'justification' && !turnChanged ? state.justification : null,
    ...(turnChanged ? { selfPrediction: null, skipOffer: null } : {}),
  };
}

function reduce(state: GameState, message: ServerMessage): Partial<GameState> {
  switch (message.t) {
    // The room has no record of this device and there are seats going spare.
    // Photos for them arrive as `player_photo` frames straight after, so the
    // picker draws license cards rather than a list of names.
    case 'claimable_seats':
      return { claimableSeats: message.seats, canJoinAsNew: message.canJoinAsNew };

    case 'welcome':
      return {
        selfId: message.selfId,
        isHost: message.isHost,
        error: null,
        // We have a seat; there is nothing left to pick from.
        claimableSeats: null,
        /*
          Onboarding is behind us if the *server* says this seat has a face on
          it. `connect()` seeds this from the stored session's `onboarded` flag,
          which is right for an ordinary reconnect and useless for the two cases
          that have no stored session: a seat claimed through the picker
          (SPEC §5.4), and any device whose storage was wiped. Both were sent
          back to the selfie screen to re-take a photo the room already had.

          The snapshot is the better authority anyway — it cannot be stale, and
          it cannot claim a photo landed when it didn't.
        */
        profileSubmitted:
          state.profileSubmitted ||
          message.snapshot.players.find((p) => p.id === message.selfId)?.hasPhoto === true,
        // A fresh connection restates the world. The server replays the
        // game-end frame when a reconnect lands in `superlatives`/`ended`, so
        // holding the old history across `welcome` could only ever show a
        // previous game's rankings in a room that has moved on.
        rankingHistory: [],
        ...withSnapshot(state, message.snapshot),
      };

    case 'snapshot':
      return withSnapshot(state, message.snapshot);

    case 'phase_changed': {
      const base = withSnapshot(state, message.snapshot);
      // Back to the lobby means "play again": nothing from the old game survives
      // except the roster, the photos and the burned question numbers (§2.6).
      const cleared: Partial<GameState> =
        message.phase === 'lobby'
          ? {
              reveal: null,
              justification: null,
              leaderboard: null,
              superlatives: null,
              rankingHistory: [],
              nextRound: null,
              lock: null,
              selfPrediction: null,
              selfPredictionLocked: false,
            }
          : {};
      return { ...base, ...cleared };
    }

    case 'roster': {
      const snapshot = state.snapshot
        ? { ...state.snapshot, players: message.players, hostId: message.hostId }
        : null;
      const joined = snapshot !== null && message.players.length > (state.snapshot?.players.length ?? 0);
      if (joined) sound.play('join');
      return {
        snapshot,
        isHost: state.selfId !== null && message.hostId === state.selfId,
        ...(snapshot
          ? {
              estimatedDurationMs: estimateDurationMs(
                countActivePlayers(message.players),
                snapshot.settings.rounds,
                ESTIMATED_MS_PER_TURN,
              ),
            }
          : {}),
      };
    }

    case 'player_photo':
      return { photos: { ...state.photos, [message.playerId]: message.photo } };

    case 'settings':
      return {
        snapshot: state.snapshot ? { ...state.snapshot, settings: message.settings } : null,
        estimatedDurationMs: message.estimatedDurationMs,
        maxRounds: message.maxRounds,
      };

    case 'game_started':
      sound.play('fanfare');
      return {
        snapshot: state.snapshot
          ? {
              ...state.snapshot,
              turnOrder: message.turnOrder,
              turnsTotal: message.turnsTotal,
              settings: { ...state.snapshot.settings, rounds: message.rounds },
            }
          : null,
        leaderboard: null,
        superlatives: null,
        rankingHistory: [],
        gameEndedReason: null,
        // The beat belongs to the lobby; once the game starts it is spent.
        deckReshuffled: false,
      };

    case 'turn_started':
      return {
        snapshot: state.snapshot
          ? {
              ...state.snapshot,
              turn: message.turn,
              usedQuestionNumbers: message.usedQuestionNumbers,
              // Last turn's answers are last turn's. Left standing, the waiting
              // list would draw a room board for a turn nobody has predicted
              // on yet, and the lock flag would open the next turn already
              // "done" — both belong to the turn that has just ended.
              selfPredictionSubmitted: false,
              pendingPredictors: [],
            }
          : null,
        reveal: null,
        justification: null,
        lock: null,
        selfPrediction: null,
        selfPredictionEditable: true,
        selfPredictionLocked: false,
        skipOffer: null,
      };

    case 'question_revealed':
      return {
        snapshot:
          state.snapshot && state.snapshot.turn
            ? { ...state.snapshot, turn: { ...state.snapshot.turn, question: message.question } }
            : state.snapshot,
      };

    /*
      Predictors only — the turn player is sent no such frame at all, so there
      is no branch here for them (SPEC §2.3 phase 2, as amended).

      `pending` is people, not picks: connected predictors who have not locked
      in. Nothing about the *content* of anyone's prediction reaches any phone
      before the reveal, and nothing that can be differenced into it does
      either. The counts are kept because the snapshot carries them; the room
      board reads the list.
    */
    case 'prediction_count':
      return {
        snapshot: state.snapshot
          ? {
              ...state.snapshot,
              predictionsIn: message.received,
              predictionsExpected: message.expected,
              pendingPredictors: message.pending,
            }
          : null,
      };

    case 'prediction_ack':
      return {
        selfPrediction: message.prediction,
        selfPredictionEditable: message.editable,
        snapshot: state.snapshot ? { ...state.snapshot, selfPredictionSubmitted: true } : null,
      };

    case 'lock_countdown':
      // The chime on every phone (SPEC §2.3 phase 3). Fired here rather than in
      // a screen so it lands even if the user is on a different route.
      sound.play('countdown');
      return {
        serverOffsetMs: message.serverNow - Date.now(),
        lock: {
          endsAt: message.endsAt,
          durationMs: message.durationMs,
          turnIndex: state.snapshot?.turn?.index ?? null,
        },
        snapshot:
          state.snapshot && state.snapshot.turn
            ? { ...state.snapshot, turn: { ...state.snapshot.turn, lockEndsAt: message.endsAt } }
            : state.snapshot,
      };

    /*
      The room's guillotine, not this seat's Submit. It closes editing for
      everyone and marks nobody as having locked in — a player who never pressed
      Submit is not made to have pressed it, and their screen does not swap out
      from under them at zero (DESIGN §3.5). Their complete pair scores all the
      same (SPEC §2.3 phase 3).
    */
    case 'predictions_locked':
      return { selfPredictionEditable: false, lock: null };

    case 'reveal':
      sound.playAt('reveal', message.reveal.startAt - state.serverOffsetMs);
      return { reveal: message.reveal, lock: null };

    case 'justification':
      return {
        justification: {
          turnPlayerId: message.turnPlayerId,
          question: message.question,
          selfVoted: message.selfVoted,
          ranking: message.ranking,
        },
      };

    case 'round_leaderboard':
      sound.play('fanfare');
      return { leaderboard: message.leaderboard, nextRound: message.nextRound };

    case 'superlatives':
      return {
        leaderboard: message.leaderboard,
        superlatives: message.superlatives,
        rankingHistory: message.history,
      };

    case 'game_ended':
      return { gameEndedReason: message.reason };

    case 'game_reset':
      return {
        reveal: null,
        justification: null,
        leaderboard: null,
        superlatives: null,
        rankingHistory: [],
        nextRound: null,
        gameEndedReason: null,
        lock: null,
        selfPrediction: null,
        selfPredictionEditable: true,
        skipOffer: null,
        // SPEC §2.6 — used numbers normally stay burned across a play-again.
        // The server says outright when it had to wipe the grid instead.
        deckReshuffled: message.deckReshuffled,
        ...withSnapshot(state, message.snapshot),
      };

    case 'turn_skipped':
      return { skipOffer: null, lock: null };

    case 'skip_offered':
      return { skipOffer: message.turnPlayerId };

    case 'error':
      return {
        error: {
          code: message.code,
          message: message.message,
          fatal: message.fatal,
          at: Date.now(),
        },
      };

    case 'pong':
      return {
        serverOffsetMs: message.serverNow - Date.now(),
        rttMs: Date.now() - message.sentAt,
      };

    default: {
      // Exhaustiveness: adding a server message without handling it is a
      // compile error rather than a silent no-op.
      const never: never = message;
      void never;
      return {};
    }
  }
}

/**
 * Persist "this seat finished onboarding" only once the **server** says the
 * seat has a photo. Optimism here is what let a dropped `set_profile` leave a
 * player in the lobby believing they had a card, and skip the photo step on
 * every future reconnect.
 */
function confirmOnboarding(state: GameState): void {
  if (!state.roomCode || !state.selfId || !state.snapshot) return;
  const self = state.snapshot.players.find((player) => player.id === state.selfId);
  if (self?.hasPhoto) markOnboarded(state.roomCode);
}

/** Players who still hold a seat. Removed players stay rankable (SPEC §5.4). */
function countActivePlayers(players: readonly RosterEntry[]): number {
  return players.filter((player) => !player.removed).length;
}

/* -------------------------------------------------------------------------- */
/* Test helpers                                                                */
/* -------------------------------------------------------------------------- */

/** Reset to a pristine store. Tests only. */
export function resetGameStore(): void {
  teardownSocket();
  useGameStore.setState({ ...INITIAL, draft: { ...EMPTY_DRAFT }, muted: sound.muted }, false);
}

/** The lobby's starting point before the first `settings` frame arrives. */
export { DEFAULT_SETTINGS };
